import { useState, useRef, useCallback } from 'react'
import { type NotaItem, RelTime, DeleteBtn, MicButton, EditableLegenda } from './CapturaControls'

// ─── ItemFoto ─────────────────────────────────────────────
interface ItemFotoProps {
  nota: NotaItem
  updateNota?: (id: string | number, data: Record<string, unknown>) => void
  addLegendaVoz: (notaId: string | number, blob: Blob) => Promise<void>
  deleteNota: (nota: NotaItem) => void
  onOpenLightbox?: (nota: NotaItem) => void
  onOpenAnnotate?: (nota: NotaItem) => void
}

export function ItemFoto({ nota, updateNota, addLegendaVoz, deleteNota, onOpenLightbox, onOpenAnnotate }: ItemFotoProps) {
  const isUploading = nota._uploading
  const hasError    = nota._error

  return (
    <div className="flex gap-3 items-start">
      <div className="relative shrink-0">
        {nota.signedUrl ? (
          <img
            src={nota.signedUrl}
            alt={nota.legenda ?? 'Foto'}
            className="w-16 h-16 object-cover rounded-xl bg-white/[0.04] cursor-pointer active:opacity-80"
            onClick={() => !isUploading && onOpenLightbox?.(nota)}
          />
        ) : (
          <div className="w-16 h-16 rounded-xl flex items-center justify-center bg-white/[0.04]">
            <span className="material-symbols-outlined text-[#555] text-2xl">image</span>
          </div>
        )}
        {isUploading && (
          <div className="absolute inset-0 rounded-xl flex items-center justify-center bg-black/50">
            <span className="material-symbols-outlined text-white animate-spin text-lg">progress_activity</span>
          </div>
        )}
        {hasError && (
          <div className="absolute inset-0 rounded-xl flex items-center justify-center bg-black/60">
            <span className="material-symbols-outlined text-lg" style={{ color: 'var(--color-vermelho)' }}>error</span>
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-[#666] uppercase tracking-wide font-bold">Foto</span>
          <RelTime iso={nota.criado_em} />
          <div className="ml-auto flex items-center gap-1">
            {nota.signedUrl && !isUploading && onOpenAnnotate && (
              <button
                onClick={() => onOpenAnnotate(nota)}
                className="w-6 h-6 flex items-center justify-center rounded-full shrink-0 border-none cursor-pointer transition-colors bg-white/[0.05] hover:bg-white/[0.10]"
                title="Riscar sobre foto"
              >
                <span className="material-symbols-outlined text-[13px]" style={{ color: '#555' }}>draw</span>
              </button>
            )}
            {!isUploading && <MicButton notaId={nota.id} addLegendaVoz={addLegendaVoz} hasLegenda={!!nota.legenda_audio_path} />}
            {!isUploading && <DeleteBtn onDelete={() => deleteNota(nota)} />}
          </div>
        </div>
        {!isUploading && updateNota && <EditableLegenda nota={nota} updateNota={updateNota} />}
        {isUploading && <span className="text-[10px] text-[#555]">Enviando...</span>}
        {hasError && <span className="text-[10px]" style={{ color: 'var(--color-vermelho)' }}>{nota._error}</span>}
        {nota.legenda_audio_path && (
          <span className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--color-verde)' }}>
            <span className="material-symbols-outlined text-[11px]">mic</span>
            Legenda gravada
          </span>
        )}
      </div>
    </div>
  )
}

// ─── ItemAudio ────────────────────────────────────────────
interface ItemAudioProps {
  nota: NotaItem
  deleteNota: (nota: NotaItem) => void
}

export function ItemAudio({ nota, deleteNota }: ItemAudioProps) {
  return (
    <div className="flex gap-3 items-center">
      <div className="w-10 h-10 rounded-xl shrink-0 flex items-center justify-center" style={{ background: 'rgba(107,79,216,0.15)' }}>
        <span className="material-symbols-outlined text-xl" style={{ color: 'var(--color-roxo)' }}>mic</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-1">
          <span className="text-[10px] text-[#666] uppercase tracking-wide font-bold">Áudio</span>
          {nota.duracao_segundos && <span className="text-[10px] text-[#555]">{nota.duracao_segundos}s</span>}
          <RelTime iso={nota.criado_em} />
          <div className="ml-auto"><DeleteBtn onDelete={() => deleteNota(nota)} /></div>
        </div>
        {nota.signedUrl && (
          <audio controls src={nota.signedUrl} className="w-full h-8" style={{ accentColor: 'var(--color-roxo)' }} />
        )}
      </div>
    </div>
  )
}

// ─── ItemTexto ────────────────────────────────────────────
interface ItemTextoProps {
  nota: NotaItem
  updateNota: (id: string | number, data: Record<string, unknown>) => void
  deleteNota: (nota: NotaItem) => void
}

export function ItemTexto({ nota, updateNota, deleteNota }: ItemTextoProps) {
  const [editing, setEditing] = useState<boolean>(false)
  const [text,    setText]    = useState<string>(nota.conteudo ?? '')
  const textRef = useRef<HTMLTextAreaElement>(null)

  const save = useCallback(() => {
    setEditing(false)
    const trimmed = text.trim()
    if (trimmed && trimmed !== (nota.conteudo ?? '')) {
      updateNota(nota.id, { conteudo: trimmed })
    } else if (!trimmed) {
      setText(nota.conteudo ?? '')
    }
  }, [text, nota.id, nota.conteudo, updateNota])

  return (
    <div className="flex gap-3 items-start">
      <div className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center mt-0.5" style={{ background: 'rgba(196,212,74,0.10)' }}>
        <span className="material-symbols-outlined text-[16px]" style={{ color: 'var(--color-hero)' }}>notes</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="text-[10px] text-[#666] uppercase tracking-wide font-bold">Texto</span>
          <RelTime iso={nota.criado_em} />
          <div className="ml-auto flex items-center gap-1">
            {!editing && (
              <button
                onClick={() => { setEditing(true); setTimeout(() => textRef.current?.focus(), 50) }}
                className="w-6 h-6 flex items-center justify-center rounded-full shrink-0 border-none cursor-pointer transition-colors bg-transparent hover:bg-white/[0.06]"
                title="Editar"
              >
                <span className="material-symbols-outlined text-[13px]" style={{ color: '#555' }}>edit</span>
              </button>
            )}
            <DeleteBtn onDelete={() => deleteNota(nota)} />
          </div>
        </div>
        {editing ? (
          <textarea
            ref={textRef}
            autoFocus
            value={text}
            onChange={e => setText(e.target.value)}
            onBlur={save}
            rows={3}
            className="w-full text-[13px] text-[#ddd] bg-white/[0.06] rounded-lg px-2 py-1.5 border border-white/[0.10] outline-none resize-none placeholder:text-[#444] leading-relaxed"
          />
        ) : (
          <p
            className="text-[13px] text-[#ddd] leading-relaxed cursor-pointer"
            onClick={() => { setEditing(true); setTimeout(() => textRef.current?.focus(), 50) }}
          >
            {nota.conteudo}
          </p>
        )}
      </div>
    </div>
  )
}

// ─── ItemDesenho ──────────────────────────────────────────
interface ItemDesenhoProps {
  nota: NotaItem
  addLegendaVoz: (notaId: string | number, blob: Blob) => Promise<void>
  deleteNota: (nota: NotaItem) => void
  onOpenLightbox?: (nota: NotaItem) => void
}

export function ItemDesenho({ nota, addLegendaVoz, deleteNota, onOpenLightbox }: ItemDesenhoProps) {
  return (
    <div className="flex gap-3 items-start">
      {nota.signedUrl ? (
        <img
          src={nota.signedUrl}
          alt="Croqui"
          className="w-16 h-16 object-cover rounded-xl shrink-0 bg-white cursor-pointer active:opacity-80"
          onClick={() => onOpenLightbox?.(nota)}
        />
      ) : (
        <div className="w-16 h-16 rounded-xl shrink-0 flex items-center justify-center bg-white/[0.04]">
          <span className="material-symbols-outlined text-[#555] text-2xl">draw</span>
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-[#666] uppercase tracking-wide font-bold">Croqui</span>
          <RelTime iso={nota.criado_em} />
          <div className="ml-auto flex items-center gap-1">
            <MicButton notaId={nota.id} addLegendaVoz={addLegendaVoz} hasLegenda={!!nota.legenda_audio_path} />
            <DeleteBtn onDelete={() => deleteNota(nota)} />
          </div>
        </div>
      </div>
    </div>
  )
}
